/**
 * Геометрия консоли: доли, а не пиксели.
 *
 * ПОЧЕМУ ДОЛИ. Эталон обмерян при ширине 1470 (docs/reference/LIVECADE_UX.md,
 * раздел 2): левая 400, центр 650, правая 420. Записать эти числа в пикселях
 * значит получить верную раскладку ровно на одной ширине: на 1280 центр
 * съёжится до 310 и перестанет вмещать кадр, на 1920 колонки повиснут узкими
 * полосами у краёв. Доли переносимы, пиксели - нет.
 *
 * Пиксели остаются только там, где они и есть физическая величина: высота
 * верхней полосы, минимальная ширина колонки, толщина границы.
 */

/** Ширина, при которой снят эталон. Доли ниже - её производные. */
export const REFERENCE_WIDTH = 1470;

/** Доли колонок эталона. Сумма равна единице по построению. */
export const REFERENCE = {
  left: 400 / REFERENCE_WIDTH, // 0,27211
  center: 650 / REFERENCE_WIDTH, // 0,44218
  right: 420 / REFERENCE_WIDTH, // 0,28571
} as const;

/** Высота верхней полосы, пиксели: полоса не тянется с шириной окна. */
export const TOPBAR_HEIGHT = 48;

/** Наименьшая высота подвала левой колонки, пиксели: одна строка кнопок. Узкая колонка переносит подвал во второй ряд (единица 21). */
export const FOOTER_HEIGHT = 38;

/**
 * Доля высоты правой колонки под областью падов.
 *
 * Эталон: тело панели падов 254 из 723 пикселей высоты под верхней полосой.
 */
export const REFERENCE_PADS = 254 / 723;

/**
 * Пределы колонок в пикселях.
 *
 * Нижние границы выбраны так, чтобы доли эталона держались до ширины 1024:
 * левая 0,272 × 1024 = 279, правая 0,286 × 1024 = 293. Ниже 1024 раскладка
 * не сжимается, а перекомпоновывается - три колонки в одну (см. ConsoleShell).
 */
export const LIMITS = {
  leftMin: 240,
  leftMax: 560,
  rightMin: 260,
  rightMax: 600,
  centerMin: 320,
  padsMinFraction: 0.12,
  padsMaxFraction: 0.72,
} as const;

/** Ширина, ниже которой три колонки заменяются одной с переключателем. */
export const STACK_BELOW = 1024;

export interface ConsoleGeometry {
  /** Доля ширины окна под левой колонкой. */
  left: number;
  /** Доля ширины окна под правой колонкой. */
  right: number;
  /** Доля высоты правой колонки под областью падов. */
  pads: number;
}

/**
 * Заводские доли консоли - НЕ доли эталона. Решение владельца 2026-09-29 (2).
 *
 * У эталона центр 650 из 1470, и кадр 9:16 во всю высоту колонки (852 px
 * при окне 900) занимает в нём 466 px: по ~90 px пустоты с каждой стороны
 * даже после того, как строка над кадром ушла (находка 10 аудита). Центр
 * ужат до кадра во всю высоту плюс столбец инструментов справа: 12 + 466 +
 * 8 + 38 + 12 ≈ 540 px. Освободившиеся 110 px поделены поровну между
 * боковыми колонками: левая 455, правая 475.
 *
 * Доли эталона (`REFERENCE`) остаются записью обмера; расхождение с ним
 * названо в `docs/DIVERGENCE.md`. Сохранённую человеком геометрию правка не
 * трогает: она лежит в браузере под своим ключом.
 */
export const DEFAULT_CENTER = 540 / REFERENCE_WIDTH;

export const DEFAULT_GEOMETRY: ConsoleGeometry = {
  left: 455 / REFERENCE_WIDTH, // 0,30952
  right: 475 / REFERENCE_WIDTH, // 0,32313
  pads: REFERENCE_PADS,
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Приведение долей к пределам при известной ширине окна.
 *
 * Ограничения заданы в пикселях, а хранится доля: пересчёт обязан идти в обе
 * стороны, иначе на узком окне сохранённая доля даст колонку в сто пикселей,
 * а на широком - в половину экрана.
 */
export function clampGeometry(geometry: ConsoleGeometry, width: number): ConsoleGeometry {
  const left = clamp(
    geometry.left * width,
    LIMITS.leftMin,
    Math.min(LIMITS.leftMax, width - LIMITS.centerMin - LIMITS.rightMin),
  );
  const right = clamp(
    geometry.right * width,
    LIMITS.rightMin,
    Math.min(LIMITS.rightMax, width - LIMITS.centerMin - left),
  );

  return {
    left: left / width,
    right: right / width,
    pads: clamp(geometry.pads, LIMITS.padsMinFraction, LIMITS.padsMaxFraction),
  };
}

/** Ключ хранилища. Геометрия запоминается между визитами - требование этапа. */
export const GEOMETRY_STORAGE_KEY = 'efira.console.geometry.v1';

export function readGeometry(): ConsoleGeometry {
  if (typeof window === 'undefined') return DEFAULT_GEOMETRY;

  try {
    const raw = window.localStorage.getItem(GEOMETRY_STORAGE_KEY);
    if (raw === null) return DEFAULT_GEOMETRY;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_GEOMETRY;

    const value = parsed as Partial<Record<keyof ConsoleGeometry, unknown>>;
    const pick = (key: keyof ConsoleGeometry) =>
      typeof value[key] === 'number' && Number.isFinite(value[key])
        ? (value[key] as number)
        : DEFAULT_GEOMETRY[key];

    return { left: pick('left'), right: pick('right'), pads: pick('pads') };
  } catch {
    // Хранилище может быть недоступно (приватное окно, запрет сайту). Консоль
    // обязана открыться с раскладкой эталона, а не упасть.
    return DEFAULT_GEOMETRY;
  }
}

export function writeGeometry(geometry: ConsoleGeometry): void {
  try {
    window.localStorage.setItem(GEOMETRY_STORAGE_KEY, JSON.stringify(geometry));
  } catch {
    // Молча: несохранённая ширина колонки - не повод показывать ошибку.
  }
}
